import {
  Tag,
  Progress,
  Space,
  Tooltip,
  Typography,
  Row,
  Col,
  Image,
  Dropdown,
  message,
  Button,
} from "antd";
import { RightOutlined, MoreOutlined } from "@ant-design/icons";
import React from "react";
import { motion } from "framer-motion";
import colors from "../../../../../utils/theme/colors";
import momentLocal from "../../../../../utils/momentLocal";
import { VideoPreview } from "../../../../../components/VideoPreview";


const statusTag = (status) => {
  if (status === "pending") return <Tag color="default">Pending</Tag>;
  if (status === "processing") return <Tag color="blue">Processing</Tag>;
  // Hide tag for completed since left accent conveys it
  if (status === "completed") return null;
  if (status === "failed") return <Tag color="red">Failed</Tag>;
  return <Tag>{status}</Tag>;
};

const statusProgress = (status) => {
  if (status === "pending")
    return <Progress percent={10} size="small" showInfo={false} />;
  if (status === "processing")
    return <Progress percent={60} size="small" showInfo={false} />;
  if (status === "completed") return null;
  if (status === "failed")
    return (
      <Progress
        percent={100}
        status="exception"
        size="small"
        showInfo={false}
      />
    );
  return null;
};

// Small status accent color for left edge
const statusAccent = (status) => {
  if (status === "completed") return "#49aa19"; // green
  if (status === "processing") return "#1677ff"; // blue
  if (status === "failed") return "#ff4d4f"; // red
  if (status === "pending") return "#8c8c8c"; // gray
  return "#8c8c8c";
};

function ImageJobItem({ item: job, handleJobClick, isGalleryView = false }) {
  const accentColor = statusAccent(job?.status);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isCopyHovered, setIsCopyHovered] = React.useState(false);

  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(text));
      } else {
        const ta = document.createElement("textarea");
        ta.value = String(text);
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      message.success("Copied to clipboard");
    } catch (e) {
      message.error("Copy failed");
    }
  };

  const handleCopyId = (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (job?.image_job_id) copyToClipboard(job.image_job_id);
  };

  const handleCopyUrls = (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    const urls = Array.isArray(job?.results)
      ? job.results
          .map((r) => r?.external_url)
          .filter((u) => typeof u === "string" && u)
      : [];
    if (urls.length > 0) copyToClipboard(urls.join("\n"));
    else message.info("No URLs to copy");
  };

  const handleDownloadImages = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    const items = Array.isArray(job?.results)
      ? job.results.filter(
          (r) =>
            r?.external_url && (r?.content_type === "image" || !r?.content_type)
        )
      : [];
    if (items.length === 0) {
      message.info("No images to download");
      return;
    }
    let success = 0;
    for (let i = 0; i < items.length; i += 1) {
      const r = items[i];
      const url = r.content || r.external_url;
      try {
        const res = await fetch(url, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const extGuess =
          blob.type && blob.type.includes("/")
            ? blob.type.split("/")[1]
            : "jpg";
        const ext = extGuess === "jpeg" ? "jpg" : extGuess;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = r?.image_id
          ? `${r.image_id}.${ext}`
          : `image_${i + 1}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        success += 1;
      } catch (err) {
        // If fetching fails (likely due to CORS), we avoid opening a new tab.
        // The final summary message will reflect partial/failed downloads.
      }
    }
    if (success === 0) message.error("Download failed");
    else if (success < items.length)
      message.warning(`Downloaded ${success} of ${items.length}`);
    else
      message.success(
        `Downloaded ${items.length} image${items.length > 1 ? "s" : ""}`
      );
  };

  if (isGalleryView) {
    // Gallery card view - visual-first with large thumbnails and hover actions
    const imageResult = Array.isArray(job?.results)
      ? job.results.find(
          (r) =>
            (r?.external_url || r?.content) &&
            (r?.content_type === "image" || !r?.content_type)
        )
      : null;
    const hasImage = job.status === "completed" && !!imageResult;
    const videoResult = Array.isArray(job?.results)
      ? job.results.find(
          (r) =>
            (r?.external_url || r?.content) && r?.content_type === "video"
        )
      : null;
    const hasVideo = job.status === "completed" && !!videoResult;
    const textResult = Array.isArray(job?.results)
      ? job.results.find(
          (r) => r?.content && r?.content_type === "text"
        )
      : null;
    const hasText = job.status === "completed" && !!textResult;

    return (
      <motion.div
        role="button"
        onClick={() => handleJobClick(job)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          background: "transparent",
          border: "none",
          borderRadius: 0,
          overflow: "hidden",
          cursor: "pointer",
          transition: "opacity 0.3s ease, transform 0.3s ease",
          position: "relative",
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Image/Video Preview Area; non-hover overlays only */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            width: "100%",
            lineHeight: 0,
            height: 0,
            paddingTop: "100%", // square aspect ratio
          }}
        >
          {hasImage ? (
            <img
              src={imageResult.external_url || imageResult.content}
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "cover",
                transition: "filter 0.2s ease",
                filter: isHovered ? "blur(2px) brightness(0.7)" : "none",
              }}
            />
          ) : hasVideo ? (
            <video
              src={videoResult.external_url || videoResult.content}
              muted
              playsInline
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : hasText ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(59, 130, 246, 0.15))",
                display: "flex",
                flexDirection: "column",
                padding: "8px",
                paddingTop: "28px",
                filter: (isHovered && !isCopyHovered) ? "blur(2px) brightness(0.7)" : "none",
                transition: "filter 0.2s ease"
              }}
            >
              {/* Text icon - smaller header */}
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 3,
                marginBottom: 3
              }}>
                <span style={{ fontSize: 12 }}>📝</span>
                <Typography.Text style={{ 
                  fontSize: 8, 
                  fontWeight: 600,
                  color: "#22c55e"
                }}>
                  TEXT
                </Typography.Text>
              </div>
              
              {/* Text preview - much bigger */}
              <div style={{ 
                flex: 1,
                display: "flex",
                flexDirection: "column",
                paddingRight: 28,
                paddingBottom: 32
              }}>
                <Typography.Text 
                  style={{ 
                    fontSize: 10, 
                    color: "rgba(255,255,255,0.95)",
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {textResult?.content}
                </Typography.Text>
              </div>
              
              {/* Copy button */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(textResult?.content || '');
                }}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  setIsCopyHovered(true);
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation();
                  setIsCopyHovered(false);
                }}
                style={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  width: 20,
                  height: 20,
                  borderRadius: 3,
                  background: isCopyHovered ? "rgba(34, 197, 94, 0.5)" : "rgba(34, 197, 94, 0.3)",
                  border: "1px solid rgba(34, 197, 94, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  zIndex: 10
                }}
              >
                <span style={{ fontSize: 10 }}>📋</span>
              </div>
            </div>
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  job.status === "failed"
                    ? "rgba(255, 77, 79, 0.12)"
                    : "rgba(255,255,255,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: job.status === "failed" ? "#ff4d4f" : "rgba(255,255,255,0.75)",
                fontSize: 13,
              }}
              title={job.status === "failed" && job.error ? String(job.error) : undefined}
            >
              <span>
                {job.status === "processing" || job.status === "pending"
                  ? "Processing…"
                  : job.status === "failed"
                  ? "Failed"
                  : "Queued"}
              </span>
            </div>
          )}

          {/* Model name tag (top-right) */}
          {job?.model_name && (
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                maxWidth: "calc(100% - 16px)",
              }}
            >
              <Tooltip title={job.model_name}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #1890ff, #69c0ff)",
                    color: "#fff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  <span
                    style={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: "18px",
                    }}
                  >
                    {job.model_name}
                  </span>
                </div>
              </Tooltip>
            </div>
          )}

          {/* Hover overlay - show only 'More info' */}
          {(hasImage || hasVideo) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: isHovered ? "auto" : "none",
              }}
            >
              <Button
                size="middle"
                type="primary"
                ghost
                shape="round"
                icon={<RightOutlined />}
                style={{
                  color: "#fff",
                  borderColor: "#fff",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                  backdropFilter: "blur(2px)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleJobClick(job);
                }}
              >
                More info
              </Button>
            </motion.div>
          )}
        </div>

      </motion.div>
    );
  }

  // Original list view
  return (
    <motion.div
      role="button"
      onClick={() => handleJobClick(job)}
      style={{
        background: colors.black,
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 10,
        padding: 12,
        cursor: "pointer",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        position: "relative",
        boxShadow: `inset 2px 0 0 ${accentColor}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
      }}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -1 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 8,
          minWidth: 0,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Tooltip title={job.image_job_id}>
            <Typography.Text strong ellipsis style={{ maxWidth: 300 }}>
              {job.image_job_id}
            </Typography.Text>
          </Tooltip>
        </div>
      </div>
      <Row gutter={[8, 8]} align="middle">
        <Col flex="auto">
          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "center",
              }}
            >
              {job.created_at && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {momentLocal(job.created_at).fromNow()}
                </Typography.Text>
              )}
              {typeof job.duration === "number" && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  • {job.duration}s
                </Typography.Text>
              )}
              {job?.model_name && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  • {job.model_name}
                </Typography.Text>
              )}
              {typeof job.cost_to_client === "number" &&
                job.cost_to_client > 0 && (
                  <Typography.Text style={{ fontSize: 12, color: "green" }}>
                    • $
                    {job.cost_to_client > 0
                      ? Number(job.cost_to_client)?.toFixed(4)
                      : 0}
                  </Typography.Text>
                )}
              {Array.isArray(job?.results) && job.results.length > 0 && (
                <Tag color="geekblue" style={{ marginInlineStart: 0 }}>
                  {job.results.length} outputs
                </Tag>
              )}
              {statusTag(job.status)}
            </div>
            {statusProgress(job.status)}
            {job.status === "failed" && job.error && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                Error: {job.error}
              </Typography.Text>
            )}
            {/* Seed / Negative chips */}
            {(() => {
              const seed =
                (job?.params && job.params.seed) ?? job?.seed ?? null;
              const negative =
                (job?.params &&
                  (job.params.negative_prompt || job.params.negative)) ||
                job?.negative_prompt ||
                null;
              if (!seed && !negative) return null;
              return (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {seed !== null && (
                    <Tag color="default" style={{ marginInlineStart: 0 }}>
                      seed: {String(seed)}
                    </Tag>
                  )}
                  {negative && (
                    <Tag color="default" style={{ marginInlineStart: 0 }}>
                      <Tooltip title={String(negative)}>
                        <span
                          style={{
                            maxWidth: 220,
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          neg: {String(negative)}
                        </span>
                      </Tooltip>
                    </Tag>
                  )}
                </div>
              );
            })()}
            {/* Prompt preview (if any) */}
            {(() => {
              const prompt =
                (job?.params &&
                  (job.params.prompt || job.params.input || job.params.text)) ||
                job?.prompt ||
                job?.input ||
                null;
              if (!prompt) return null;
              return (
                <Tooltip title={String(prompt)}>
                  <Typography.Text
                    type="secondary"
                    ellipsis
                    style={{ fontSize: 12, display: "block", width: "100%" }}
                  >
                    {String(prompt)}
                  </Typography.Text>
                </Tooltip>
              );
            })()}
          </Space>
        </Col>
      </Row>
      {/* Images section */}
      {job.status === "completed" &&
      Array.isArray(job.results) &&
      job.results.length > 0 ? (
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          {job.results.map((res, idx) => {
            const key = res?.image_id || res?.id || res?.external_url || idx;
            if (res?.content_type === "video") {
              return (
                <Col key={key}>
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid #1f1f1f",
                      background: "#141414",
                    }}
                  >
                    <VideoPreview
                      src={res.external_url}
                      height={96}
                      width={96}
                      pressable={false}
                    />
                  </div>
                </Col>
              );
            }
            if (
              res?.content_type === "image" &&
              (res?.content || res?.external_url)
            ) {
              return (
                <Col key={key}>
                  <Image
                    preview={false}
                    width={96}
                    height={96}
                    src={res.external_url}
                    alt={String(key)}
                    style={{
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #1f1f1f",
                      background: "#141414",
                    }}
                    placeholder
                  />
                </Col>
              );
            }
            if (res?.content_type === "text" && res?.content) {
              return (
                <Col key={key}>
                  <Tooltip title={res.content}>
                    <div
                      style={{
                        width: 96,
                        height: 96,
                        padding: 8,
                        borderRadius: 8,
                        border: "1px solid #1f1f1f",
                        background: "#141414",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography.Text style={{ fontSize: 12 }} ellipsis>
                        {res.content}
                      </Typography.Text>
                    </div>
                  </Tooltip>
                </Col>
              );
            }
            if (res?.external_url) {
              return (
                <Col key={key}>
                  <Image
                    preview={false}
                    width={96}
                    height={96}
                    src={res.external_url}
                    alt={String(key)}
                    style={{
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #1f1f1f",
                      background: "#141414",
                    }}
                    placeholder
                  />
                </Col>
              );
            }
            return null;
          })}
        </Row>
      ) : job.status === "processing" || job.status === "pending" ? (
        <Row style={{ marginTop: 8 }}>
          <Col>
            <Typography.Text type="secondary">
              Generating...
            </Typography.Text>
          </Col>
        </Row>
      ) : null}
      {/* Bottom-right actions */}
      <div
        style={{
          position: "absolute",
          right: 8,
          bottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "rgba(255,255,255,0.55)",
        }}
      >
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "copyId", label: "Copy ID" },
              { key: "copyUrls", label: "Copy URLs" },
              { key: "downloadImages", label: "Download images" },
            ],
            onClick: ({ key, domEvent }) => {
              if (domEvent?.stopPropagation) domEvent.stopPropagation();
              if (key === "copyId") handleCopyId();
              if (key === "copyUrls") handleCopyUrls();
              if (key === "downloadImages") handleDownloadImages();
            },
          }}
        >
          <span
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: 4,
              borderRadius: 6,
              border: "1px solid #1f1f1f",
              background: "#141414",
              display: "inline-flex",
            }}
          >
            <MoreOutlined style={{ fontSize: 12 }} />
          </span>
        </Dropdown>
        <span
          style={{
            padding: 4,
            borderRadius: 6,
            border: "1px solid #1f1f1f",
            background: "#141414",
            display: "inline-flex",
          }}
        >
          <RightOutlined style={{ fontSize: 12 }} />
        </span>
      </div>
    </motion.div>
  );
}

export default ImageJobItem;
